import { inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { defer, EMPTY, merge, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { LoadingScreenComponent } from '../../../components/loading-screen/loading-screen.component';
import { LoadingScreenModel } from '../../../components/loading-screen/loading-screen.model';
import { ConfigConstants } from '../../../config/config-constants';
import { CONNECTION_ERROR_CODES } from '../../../enums/connection-error-codes.enum';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../../interfaces/synchronization.interface';
import { AppNotificationType } from '../../../services/app-notification/app-notification.model';
import { AppNotificationService } from '../../../services/app-notification/app-notification.service';
import { ConnectivityService } from '../../../services/connectivity/connectivity.service';
import { OdataNotificationService } from '../../../services/notification/odata-notification.service';
import { isDefined } from '../../../utilities/is-defined';
import { LogUtilities } from '../../../utilities/log-utilities';
import { NotificationUtilities } from '../../../utilities/notification-utilities';
import { addressScopeRequested, upsertAddressRequested } from '../../address/actions/address.actions';
import { selectAddresses } from '../../address/selectors/address.selector';
import { hydrateSlice, persistenceError, persistSlice, sliceHydrated, slicePersisted } from '../../persistence';
import { LegacyDataMigrationService } from '../../../services/legacy-data-migration/legacy-data-migration.service';
import { getUuidV4 } from '../../../utilities/generic-utilities';
import {
    addStatusToNotificationEntry,
    cancellationConfirmationRemoved,
    cancellationConfirmationSaved,
    hydrateNotifications,
    initNotification,
    legacyNotificationCompletionsMigrated,
    markNotificationCompleted,
    notificationFailed,
    notificationLoaded,
    notificationRefreshRequired,
    notificationRestoredFromStorage,
    notificationStorageSyncError,
    persistNotificationsRequested,
    removeExpiredCompletedNotifications,
    setIncorrectNotifications,
    setNotificationCompletions,
    setNotificationEntries,
    setNotificationStatusFields,
    showNoEquipmentNotificationList,
    travelStatusRecorded,
    updateNotificationStatusFields,
} from '../actions/notification.actions';
import {
    completedListModelToCompletions,
    completedNotificationsToListModel,
    ConvertedNotificationEntry,
    odataToEntry,
    resolveConvertedAddresses,
} from '../notification-collection.converters';
import {
    NOTIFICATION_FEATURE_KEY,
    NotificationCompletionsState,
    NotificationState,
    NotificationStatusRecordState,
} from '../notification-collection.state';
import {
    selectCompletions,
    selectEntryIdsMissingStartEndStatus,
    selectLastNotificationLoad,
    selectNotificationEntries,
    selectNotificationEntryById,
    selectNotificationInitialized,
} from '../selectors/notification.selector';
import { NotificationIdState } from '../../utilities/notification-id.type';
import { NotificationStateUtilities } from '../../utilities/notification-state-utlities';

type PersistedData = {
    entries: NotificationState[];
    /** Absent from a cache written before the completion records moved off the entries. */
    completions?: NotificationCompletionsState;
};

/**
 * Owns the notification load/persist flow and every side effect around it: handing customer
 * addresses to the address slice, backfilling statuses, and reporting what the backend sent wrong.
 *
 * `init$` stays one long effect rather than several: its steps have genuine ordering dependencies
 * on each other that the "one effect per action" pattern used by the other effects on this class
 * cannot express. It reconciles the fresh entries in hand and dispatches them once, as
 * `notificationLoaded`, rather than writing each intermediate result to the store: the entries it
 * is deriving from are not in the store yet, so it uses the shared model-free rules exported by
 * `notification.selector.ts` rather than the selectors built on top of them.
 *
 * It works on `NotificationState`s throughout, never on `NotificationModel`/`StatusModel`. The
 * config, user-master, timesheet and address domains are migrated too, so their data is read from
 * their slices and written with their actions. Those writes are dispatch-and-continue: the store is
 * the source of truth and a reducer applies synchronously, so nothing here has to wait for the
 * owning slice to mirror the write to storage — reporting a failed persist is that slice's own
 * business. The addresses a load carries are handed to the address slice one at a time, which owns
 * what folding one in means and normalizes it on the way; all this class decides is which address
 * entry each notification links to. What a load means for the timesheet's own entries is likewise
 * the timesheet slice's to decide, off `notificationLoaded` — see
 * `TimesheetEffects.removeStaleNotificationInfo$`.
 *
 * A refresh is requested by dispatching `notificationRefreshRequired`; a completed load is announced
 * by the slice itself, through the `lastLoad` marker `setLoadedReducer` advances, which the
 * interested screens read via `notification-updates.ts`. Nothing here notifies anyone directly:
 * every screen that has to react to what these effects produce reads the slice, so this class
 * dispatches and logs, and never pushes to `EventService`.
 */
@Injectable()
export class NotificationEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly odataNotificationService = inject(OdataNotificationService);
    private readonly appNotificationService = inject(AppNotificationService);
    private readonly translate = inject(TranslateService);
    private readonly modalService = inject(BsModalService);
    private readonly connectivityService = inject(ConnectivityService);
    private readonly synchronizationService = inject<SynchronizationInterface>(SynchronizationInstanceToken);
    private readonly log: LogInterface = inject(LogInstanceToken);
    private readonly legacyDataMigration = inject(LegacyDataMigrationService);
    private readonly notificationUtilities: NotificationUtilities = inject(NotificationUtilities);

    /**
     * The stored entries: `init$` reconciles the fresh payloads against everything the device knows.
     * Read through this signal rather than snapshotted, so that `init$` sees what the store holds at
     * the moment the fetch resolves rather than when it was started.
     */
    private readonly storedEntries = this.store.selectSignal(selectNotificationEntries);

    // Config, user-master and address reads, in the same read-per-call form as `storedEntries`, so
    // each one sees the current value rather than a snapshot taken at construction time.
    private readonly addresses = this.store.selectSignal(selectAddresses);

    /**
     * Monotonic counter for the timesheet/address write requests below. Nothing here awaits their
     * outcome (see the class doc); the id only keeps the owning slice's persist bridge from
     * settling a concurrent write on this one's result.
     */
    private saveCorrelationCounter = 0;

    /** Fetch, hand the addresses over, reconcile statuses, redistribute paused work, load the slice. */
    public init$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initNotification),
            switchMap(({ isAppStart }) => {
                const wasInitialized = this.store.selectSignal(selectNotificationInitialized)();
                return this.odataNotificationService.getNotificationsWithRaw(isAppStart).pipe(
                    map((pairs) => pairs.map((pair) => odataToEntry(pair.raw))),
                    map((converted) => this.mergeAddresses(converted)),
                    mergeMap((entries) => [
                        notificationLoaded({
                            entries,
                            isAppStart,
                        }),
                        setIncorrectNotifications({
                            ids: [...this.odataNotificationService.incorrectNotificationsList],
                        }),
                        showNoEquipmentNotificationList({ showAppNotification: wasInitialized }),
                    ]),
                    catchError((error: unknown) => {
                        const message = NotificationEffects.errorMessage(error);
                        this.log.error(`NotificationEffects: init$: error in the pipe: ${message}`);
                        return of(notificationFailed({ error: message }));
                    }),
                );
            }),
        ),
    );

    /**
     * Re-runs the load when something asks for fresh notifications, but only once the slice holds
     * notifications at all — a refresh before the first load would race `init$`. Errors are
     * swallowed into `EMPTY` so a single failed refresh does not tear the effect down for good.
     */
    public refreshRequested$ = createEffect(() =>
        this.actions$.pipe(
            ofType(notificationRefreshRequired),
            concatLatestFrom(() => this.store.select(selectNotificationInitialized)),
            filter(([, initialized]) => initialized),
            tap(() => this.log.debug('NotificationEffects: notificationRefreshRequired - Refreshing notifications')),
            map(() => initNotification({ isAppStart: false })),
            catchError((error) => {
                this.log.error(`NotificationEffects: refreshRequested$: error in the pipe: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    /**
     * Logs the entries for which neither a DURA task/status nor an ETA/DISP one could be found, so
     * their start and end silently fall back to the raw plan dates. The condition is detected while
     * deriving those dates, but a pure selector has no side-effect channel to log through (see
     * `notificationStartForEntry`), so it is re-derived here via
     * `selectEntryIdsMissingStartEndStatus` after every load and logged once per affected entry.
     */
    public reportMissingStartEndStatus$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(notificationLoaded, notificationRestoredFromStorage),
                concatLatestFrom(() => this.store.select(selectEntryIdsMissingStartEndStatus)),
                tap(([, ids]) => {
                    ids.forEach((id) => {
                        this.log.error(
                            `NotificationEffects: Neither status ETA nor DISP was found in taskSet for ${NotificationStateUtilities.notificationIdToString(id)}. This is an invalid situation. Fallback value plannedStart/plannedEnd returned.`,
                        );
                    });
                }),
            ),
        { dispatch: false },
    );

    /**
     * Reports the notifications the backend sent without any equipment, and rebuilds the offline
     * store when the local copy is the incomplete one — an entry holding fewer equipments than its
     * own count claims, or none marked as the main one, means the cache is out of step with the
     * gateway.
     */
    public warnNoEquipment$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(showNoEquipmentNotificationList),
                filter(({ showAppNotification }) => showAppNotification),
                concatLatestFrom(() => this.store.select(selectNotificationEntries)),
                tap(([, entries]) => {
                    const withoutEquipmentFromGateway = entries.filter((entry) => entry.equipmentCount === 0);
                    if (withoutEquipmentFromGateway.length > 0) {
                        const body = this.translate.instant('NOTIFICATION_NO_EQUIPMENT_ERROR_BODY') as string;
                        this.appNotificationService.setAppNotification(
                            AppNotificationType.ERROR,
                            this.translate.instant('NOTIFICATION_NO_EQUIPMENT_ERROR_TITLE') as string,
                            NotificationUtilities.getNotificationsFormattedWithNewLine(
                                withoutEquipmentFromGateway.map((notification) =>
                                    this.notificationUtilities.getNotificationTypeDescriptionFor(
                                        notification.notificationType,
                                        notification.notificationId.qmNum,
                                        notification.notificationId.maNum,
                                    ),
                                ),
                                body,
                            ),
                        );
                    }

                    const withoutEquipmentFromOfflineStore = entries.filter((entry) => {
                        const hasMainEquipment = entry.equipments.some(
                            (equipment) => equipment.mainEquipmentIndicator === ConfigConstants.sapTrue,
                        );
                        return entry.equipments.length !== entry.equipmentCount || !hasMainEquipment;
                    });
                    if (withoutEquipmentFromOfflineStore.length > 0) {
                        this.recreateOfflineStore();
                    }
                }),
            ),
        { dispatch: false },
    );

    /** Tells the user which notifications the backend sent but could not be parsed. */
    public reportNotificationsNotLoaded$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(setIncorrectNotifications),
                filter(({ ids }) => ids.length > 0),
                tap(({ ids }) => {
                    this.log.warn(`Total ${ids.length} notifications are not loaded.`);
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        this.translate.instant('NOTIFICATION_NOT_LOADED_ERROR_TITLE') as string,
                        `${this.translate.instant('NOTIFICATION_NOT_LOADED_ERROR_BODY') as string} ${ids.join(',').replace(/,/g, '<br>')}`,
                    );
                }),
            ),
        { dispatch: false },
    );

    /**
     * Reads the persisted state back: once implicitly, on the app-start load, and whenever a
     * re-read is asked for explicitly.
     *
     * `isAppStart` is what makes the implicit read a once-per-app-run affair — it is set only by
     * the one `init(true)` in `InitializationService`, so every later `initNotification` is a
     * refresh, whose fetch must not be undercut by the cache it is replacing. An explicit
     * `hydrateNotifications` is never gated: getting a fresh read is the whole point of it, and it
     * is what {@link rehydrate$} and the cross-device sync rely on.
     */
    public hydrateNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initNotification, hydrateNotifications),
            filter((action) => action.type === hydrateNotifications.type || action.isAppStart),
            map(() => hydrateSlice({ featureKey: NOTIFICATION_FEATURE_KEY })),
        ),
    );

    /**
     * Translate the generic hydration result back into the notification slice. Every read this
     * slice asks for is applied, and a read that failed arrives here as a `sliceHydrated` carrying
     * no data — which restores the empty defaults over an already-empty slice. That is why the
     * slice needs no hydration status of its own: whoever asked for the read is told it finished
     * either way, and there is no recovery to choose between.
     *
     * A read that delivered data the slice cannot restore from is a different matter: nothing
     * distinguishes it from an empty cache once the entries are gone, so it is recorded on the
     * slice's `error` before the restore is applied — the restore itself still happens, and still
     * with the empty defaults, because `rehydrate$` waits for it and a cross-device sync must not
     * stall on an unreadable cache.
     */
    public applyHydratedNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === NOTIFICATION_FEATURE_KEY),
            mergeMap(({ data }) => {
                const persisted = data as PersistedData | undefined;
                if (isDefined(persisted) && !Array.isArray(persisted.entries)) {
                    const error = 'NotificationEffects: applyHydratedNotification$: persisted slice holds no entries';
                    this.log.error(error);
                    return of(
                        notificationStorageSyncError({ error }),
                        notificationRestoredFromStorage({ entries: [], completions: undefined }),
                    );
                }
                return of(
                    notificationRestoredFromStorage({
                        entries: persisted?.entries ?? [],
                        completions: persisted?.completions,
                    }),
                );
            }),
        ),
    );

    /**
     * Migrates the completion records the legacy `NotificationStatusService` left under
     * `notificationRecentlyCompleted` into the completion map, and persists them. Runs after every
     * restore rather than once, so that a restore cannot overwrite the migrated records, and so that a
     * key brought back by a download is migrated again; `LegacyDataMigrationService` keeps it inert
     * while `enableNgRx` is off. No record is filtered by age — the map's own retention applies to
     * migrated records like to any other. Needs no backend load: a completion record no longer needs
     * its notification in the collection.
     */
    public migrateLegacyCompletions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(notificationRestoredFromStorage),
            mergeMap(() => this.legacyDataMigration.readLegacyCompletions()),
            filter(isDefined),
            mergeMap(({ key, data }) => {
                const correlationId = getUuidV4();
                return of(
                    legacyNotificationCompletionsMigrated({
                        completions: completedListModelToCompletions(data),
                        keys: [key],
                        correlationId,
                    }),
                    persistSlice({ featureKey: NOTIFICATION_FEATURE_KEY, correlationId }),
                );
            }),
        ),
    );

    /**
     * Deletes the migrated legacy key once the persist carrying the migrated records has succeeded —
     * never earlier, so a failed persist leaves it for the next restore to migrate again.
     */
    public deleteMigratedLegacyCompletions$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(legacyNotificationCompletionsMigrated),
                mergeMap(({ keys, correlationId }) =>
                    this.actions$.pipe(
                        ofType(slicePersisted, persistenceError),
                        filter(
                            (action) =>
                                action.featureKey === NOTIFICATION_FEATURE_KEY &&
                                action.correlationId === correlationId,
                        ),
                        take(1),
                        mergeMap((action) => {
                            if (action.type === persistenceError.type) {
                                this.log.error(
                                    `NotificationEffects: deleteMigratedLegacyCompletions$: the migrated completions were not persisted, keeping ${keys.join(', ')}`,
                                );
                                return of(undefined);
                            }
                            return this.legacyDataMigration.deleteLegacyKeys(keys);
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );

    /**
     * Log the recently-completed notifications whenever they change. Reporting them to their
     * readers is not this effect's business: `completedNotifications$()` derives the list from the
     * same selector, so a screen that shows it re-reads it by itself. The log is the reason the
     * model still gets built here — `LogUtilities` formats the model, not the entries.
     */
    public logCompletedNotifications$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    removeExpiredCompletedNotifications,
                    setNotificationCompletions,
                    markNotificationCompleted,
                    legacyNotificationCompletionsMigrated,
                ),
                concatLatestFrom(() => this.store.select(selectCompletions)),
                tap(([, completions]) => {
                    this.log.debug(
                        `${this.constructor.name}: completed notifications updated`,
                        LogUtilities.getLogOutputCompletedNotifications(completedNotificationsToListModel(completions)),
                    );
                }),
            ),
        { dispatch: false },
    );

    /**
     * Report each status appended to a notification. Keeping this here means callers only dispatch
     * `addStatusToNotificationEntry` and never have to log the write themselves.
     */
    public logStatusAdded$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(addStatusToNotificationEntry),
                tap(({ id, status }) => this.logStatusAdded(id, status)),
            ),
        { dispatch: false },
    );

    /**
     * Reports the statuses a load appended, which the reducer records on `lastLoad` rather than
     * logging itself. The counterpart of {@link logStatusAdded$} for the load path: those appends
     * never pass through `addStatusToNotificationEntry`, since the reducer applies the whole
     * reconciliation in one step.
     */
    public logStatusesAddedByLoad$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(notificationLoaded),
                concatLatestFrom(() => this.store.select(selectLastNotificationLoad)),
                tap(([, lastLoad]) =>
                    (lastLoad?.addedStatuses ?? []).forEach(({ id, status }) => this.logStatusAdded(id, status)),
                ),
            ),
        { dispatch: false },
    );

    /**
     * Reports each travel status the device recorded, with the statuses it produced, which the
     * reducer derives from the submission and the entry rather than the dispatch carrying them.
     */
    public logTravelStatusRecorded$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(travelStatusRecorded),
                concatLatestFrom(({ id }) => this.store.select(selectNotificationEntryById(id))),
                tap(([{ id, submission }, entry]) =>
                    this.log.debug(
                        `${this.constructor.name}: recorded travel status ${submission.statusCode} on ` +
                            `${NotificationStateUtilities.notificationIdToString(id)}, statuses now`,
                        entry?.status,
                    ),
                ),
            ),
        { dispatch: false },
    );

    /**
     * Warns about every recorded status a load had to correct from the backend's tasks. Each one is
     * a case where the device's prediction of the backend (`notification-status.rules.ts`) was wrong,
     * and the times it showed until the load were wrong with it.
     */
    public warnStatusDiscrepanciesFromLoad$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(notificationLoaded),
                concatLatestFrom(() => this.store.select(selectLastNotificationLoad)),
                tap(([, lastLoad]) =>
                    (lastLoad?.statusDiscrepancies ?? []).forEach(({ id, recorded, task }) =>
                        this.log.warn(
                            `${this.constructor.name}: recorded status ${recorded.statusCode} of ` +
                                `${NotificationStateUtilities.notificationIdToString(id)} ` +
                                `(${recorded.startTime} - ${recorded.endTime}) differs from its task ` +
                                `(${task.startTime} - ${task.endTime}); the task's window was taken`,
                        ),
                    ),
                ),
            ),
        { dispatch: false },
    );

    private logStatusAdded(id: NotificationIdState, status: NotificationStatusRecordState): void {
        this.log.debug(
            `${this.constructor.name}: added status ${status.statusCode} to ` +
                `${NotificationStateUtilities.notificationIdToString(id)} ` +
                `(${status.startTime} - ${status.endTime})`,
        );
    }

    /**
     * Record a failed slice write on the slice. `persistNotification$` fires and forgets — no caller
     * awaits a notification persist the way the timesheet facade awaits its own — so without this a
     * failed serialization or write would show up in the log only, while the slice went on implying
     * that what it holds is cached.
     */
    public reportPersistenceError$ = createEffect(() =>
        this.actions$.pipe(
            ofType(persistenceError),
            filter(({ featureKey }) => featureKey === NOTIFICATION_FEATURE_KEY),
            map(({ error }) => notificationStorageSyncError({ error })),
        ),
    );

    /** Persist the slice after every mutation of a persisted field, and on explicit request. */
    public persistNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                notificationLoaded,
                setNotificationEntries,
                updateNotificationStatusFields,
                addStatusToNotificationEntry,
                travelStatusRecorded,
                setNotificationStatusFields,
                setNotificationCompletions,
                markNotificationCompleted,
                removeExpiredCompletedNotifications,
                cancellationConfirmationSaved,
                cancellationConfirmationRemoved,
                persistNotificationsRequested,
            ),
            map(() => persistSlice({ featureKey: NOTIFICATION_FEATURE_KEY })),
        ),
    );

    // ---- private helpers, shared between init$ and the standalone effects ----

    /**
     * Re-reads the slice from storage and applies the completed-notification retention policy. The
     * cross-device sync has to await this before the notification re-fetch reconciles against the
     * stored statuses, or it would work against the statuses of the previous device.
     *
     * The read is awaited through `notificationRestoredFromStorage`, which `applyHydratedNotification$`
     * emits once the restored entries are in the slice — waiting on the generic `sliceHydrated`
     * instead would resume one action too early, before the reducer has applied them. A failed read
     * settles it too, so a device that cannot read its cache still syncs. `merge` subscribes to its
     * sources in order, which is what puts the listener in place before the dispatch that triggers
     * the read.
     */
    private rehydrate$(): Observable<void> {
        return merge(
            this.actions$.pipe(ofType(notificationRestoredFromStorage), take(1)),
            defer(() => {
                this.store.dispatch(hydrateNotifications());
                return EMPTY;
            }),
        ).pipe(
            take(1),
            map(() => {
                this.store.dispatch(removeExpiredCompletedNotifications());
                return undefined;
            }),
        );
    }

    /**
     * Hands the addresses of the fresh notifications to the address slice, points every notification
     * at the entry its own address ended up as, and narrows the collection to exactly what this load
     * carried.
     *
     * The slice owns what folding an address in means — see `upsertAddressRequested`; all that is
     * decided here is which entry each notification links to, which `resolveConvertedAddresses`
     * answers against the collection as it stands. Upserts are applied by the time the dispatch
     * returns, so each notification resolves against what the ones before it contributed, and two
     * notifications at the same customer end up sharing one entry.
     *
     * The scope narrowing (`addressScopeRequested`) is dispatched once, after every notification's
     * upserts have already landed — not interleaved with them — because it has to see the ids the
     * fresh addresses actually resolved to, and those only exist in the slice once their upsert has
     * been applied. This method is only reached once the fetch has already succeeded (see `init$`'s
     * pipe), so a failed load never dispatches a scope narrowing and cannot take anything out of
     * scope.
     *
     * @param converted the freshly converted notifications with their extracted addresses
     * @returns the notifications, each pointing at the address entry it shares
     */
    private mergeAddresses(converted: ConvertedNotificationEntry[]): NotificationState[] {
        const inScopeIds = new Set<string>();
        const entries = converted.map((notification) => {
            const resolved = resolveConvertedAddresses(notification, this.addresses());
            [...resolved.addressSet, resolved.customerAddress].forEach((address) => {
                inScopeIds.add(address.id);
                this.store.dispatch(
                    upsertAddressRequested({
                        address,
                        correlationId: `notification-address-upsert-${++this.saveCorrelationCounter}`,
                    }),
                );
            });
            return resolved.entry;
        });
        this.store.dispatch(
            addressScopeRequested({
                inScopeIds: [...inScopeIds],
                correlationId: `notification-address-scope-${++this.saveCorrelationCounter}`,
            }),
        );
        return entries;
    }

    /**
     * Formats an unknown rejection value into the message the slice carries and the log shows.
     * `JSON.stringify` is kept for the plain objects the OData layer rejects with — that is what the
     * log showed before this became the slice's `error` too — but it is neither safe on every value
     * nor useful on an `Error`, so those two cases are handled first.
     *
     * @param error the rejection value
     * @returns a message describing it
     */
    private static errorMessage(error: unknown): string {
        if (error instanceof Error) {
            return error.message;
        }
        if (typeof error === 'string') {
            return error;
        }
        try {
            return JSON.stringify(error) ?? String(error);
        } catch {
            return String(error);
        }
    }

    /**
     * Throws the local offline store away and pulls everything down again, behind a blocking
     * loading screen. The last resort when the cached data is provably incomplete; needs a working
     * connection, and says so when there is none.
     */
    private recreateOfflineStore(): void {
        const loadingScreenModel = new LoadingScreenModel(
            this.translate.instant('NO_EQUIPMENT_LOADING_MESSAGE') as string,
        );
        const modalInstance = this.modalService.show(LoadingScreenComponent, {
            backdrop: 'static',
            class: 'modal-sm',
            initialState: { model: loadingScreenModel },
            ariaLabelledBy: 'loading-screen-modal-title',
        });

        loadingScreenModel.loadingScreenDisplayed$
            .pipe(
                take(1),
                mergeMap(() => this.connectivityService.isOnlineButWithErrors$),
                mergeMap(() => this.synchronizationService.flushCompletely()),
                mergeMap(() => this.synchronizationService.receiveEverything(true)),
                catchError((error) => {
                    const isNoInternetError = error === CONNECTION_ERROR_CODES.NO_INTERNET_CONNECTION;
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.WARN,
                        this.translate.instant(
                            isNoInternetError ? 'APP_SYNC_FAILED_TITLE' : 'ERROR_REQUEST_ERROR',
                        ) as string,
                        this.translate.instant(
                            isNoInternetError ? 'APP_SYNC_FAILED_SYNCHRONIZATION' : 'ERROR_NO_INTERNET_ERROR',
                        ) as string,
                    );
                    return of(undefined);
                }),
            )
            .subscribe(() => {
                modalInstance.hide();
            });
    }
}
